import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import is from 'is_js';
import humps from 'lodash-humps';
import styled from 'styled-components';

import { colors } from '@style-guide/config';

import * as calqSearchActions from '@redux/modules/calqSearch';

import {
  getUrlParameterByName,
} from '@services/Url';
import {
  ErrorMessage,
  FlexWrapper,
  LoadMoreButton,
  LoadMoreWrapper,
  ResultsCount,
  ResultsFooter,
  ResultsHeader,
  ResultsInner,
  ResultsTitle,
  ResultsWrapper,
  SortFilterLabel,
  SortFilterWrapper,
  StyledSelect,
  WarningMessage,
} from '@components/SearchResults';
import FilterCloud from '@structures/FilterCloud';
import ResourcePreviewList from '@components/ResourcePreviewList/ResourcePreviewList';

import searchFilterApiAdapter from './utils/searchFilterApiAdapter';
import sortFilterApiAdapter from './utils/sortFilterApiAdapter';

const ResultItemsWrapper = styled.div`
  > div:not(:last-of-type) {
    margin-bottom: 50px;
    border-bottom: ${colors.darkGray} 1px solid;
  }
`;

ResultItemsWrapper.displayName = 'ResultItemsWrapper';

class Results extends React.Component {
  constructor(props) {
    super(props);

    const { dispatch } = this.props;

    this.calqSearchActions = bindActionCreators(calqSearchActions, dispatch);

    this.renderResults = this.renderResults.bind(this);
    this.search = this.search.bind(this);
  }

  componentDidMount() {
    const urlSortFilters = getUrlParameterByName('sort');
    const urlSearchFilters = JSON.parse(getUrlParameterByName('search'));

    if (!urlSortFilters && !urlSearchFilters) {
      this.calqSearchActions.sortUpdate('criteria', 'mostRecent');
      this.search();
    }

    if (urlSortFilters) {
      this.calqSearchActions.sortUpdate('criteria', urlSortFilters);
    }

    if (urlSearchFilters) {
      this.calqSearchActions.filtersRestore(urlSearchFilters);
    }
  }

  componentDidUpdate(prevProps) {
    const prevSortCriteria = prevProps.calqSearch.sort.criteria;
    let sortCriteria = this.props.calqSearch.sort.criteria;
    const prevSearchCriteria = prevProps.calqSearch.filters;
    const searchCriteria = this.props.calqSearch.filters;

    if (searchCriteria.keyword === '' && searchCriteria !== prevSearchCriteria) {
      sortCriteria = 'mostRecent';
      this.calqSearchActions.sortUpdate('criteria', sortCriteria);
    }

    if (searchCriteria.keyword && prevSearchCriteria.keyword === '') {
      sortCriteria = 'relevancy';
      this.calqSearchActions.sortUpdate('criteria', sortCriteria);
    }

    window.history.replaceState(
      null,
      null,
      `?sort=${sortCriteria}&search=${encodeURIComponent(JSON.stringify(searchCriteria))}`,
    );

    if (prevSearchCriteria !== searchCriteria || prevSortCriteria !== sortCriteria) {
      this.search();
    }
  }

  search(pageOffset = 0) {
    const {
      sort,
      filters,
    } = this.props.calqSearch;

/* eslint-disable */
    const sortFilterApiData = {
      ...(sortFilterApiAdapter(sort)),
      pageNumber: sort.pageNumber + pageOffset,
      pageLength: sort.pageLength,
    };
  /* eslint-enable */

    const searchFilterApiData = searchFilterApiAdapter(filters);

    if (pageOffset) {
      this.calqSearchActions.searchLoadMore(sortFilterApiData, searchFilterApiData);
      this.calqSearchActions.sortUpdate('pageNumber', sort.pageNumber + pageOffset);
    } else {
      this.calqSearchActions.search(sortFilterApiData, searchFilterApiData);
    }
  }

  renderResults() {
    const {
      error,
      results,
    } = this.props.calqSearch;

    let component;

    if (is.not.empty(results)) {
      component = (
        <ResourcePreviewList
          resources={humps(results)}
          hasTopMargin={false}
        />
      );
    } else if (!error) {
      component = (
        <WarningMessage
          message="No results have matched your search criteria. Please widen your search."
        />
      );
    }

    return component;
  }

  render() {
    const {
      loading,
      filters,
      totalCount,
      sort,
      error,
      results,
    } = this.props.calqSearch;

    return (
      <FlexWrapper>
        <FilterCloud
          searchFilterValues={filters}
          updateSearchFilter={this.calqSearchActions.filtersUpdate}
          clearSearchFilters={this.calqSearchActions.filtersClear}
        />
        <ResultsWrapper>
          <ResultsHeader>
            <ResultsTitle>
              Results
              <span
                id="resultsCountHeader"
              >
                {` (${totalCount})`}
              </span>
            </ResultsTitle>
            <SortFilterWrapper>
              <SortFilterLabel>Sort By:</SortFilterLabel>
              <StyledSelect
                onChange={e => this.calqSearchActions.sortUpdate('criteria', e.target.value)}
                value={sort.criteria}
                id="sortByOptionsSelect"
              >
                <option
                  value="relevancy"
                  id="sortByOptionsKeywordRelevancy"
                >
                  Keyword Relevancy
                </option>
                <option
                  value="mostRecent"
                  id="sortByOptionsMostRecent"
                >
                  Recently Published
                </option>
                <option
                  value="nameAZ"
                  id="sortByOptionsNameAZ"
                >
                  Name - A-Z
                </option>
                <option
                  value="nameZA"
                  id="sortByOptionsNameZA"
                >
                  Name - Z-A
                </option>
              </StyledSelect>
            </SortFilterWrapper>
          </ResultsHeader>
          <ResultsInner>
            {(!loading || is.not.empty(results)) && (
              <div
                id="resultsContainer"
              >
                <ResultItemsWrapper>
                  {this.renderResults()}
                </ResultItemsWrapper>
              </div>
            )}
            {error && (
              <ErrorMessage
                message={error}
              />
            )}
            {(results.length < totalCount || is.not.empty(results)) && (
              <ResultsFooter>
                {is.not.empty(results) && (
                  <ResultsCount
                    id="resultsCountFooter"
                  >
                      (Showing {results.length} of {totalCount})
                  </ResultsCount>
                )}
                {results.length < totalCount && (
                  <LoadMoreWrapper>
                    <LoadMoreButton
                      id="practiceLibraryResultsLoadMore"
                      onClick={() => {
                        this.search(1);
                      }}
                    >
                        Load more +
                    </LoadMoreButton>
                  </LoadMoreWrapper>
                )}
              </ResultsFooter>
            )}
          </ResultsInner>
        </ResultsWrapper>
      </FlexWrapper>
    );
  }
}

Results.propTypes = {
  calqSearch: PropTypes.shape({
    error: PropTypes.string,
    filters: PropTypes.shape({
      keyword: PropTypes.string,
      areasOfLaw: PropTypes.arrayOf(PropTypes.object),
      publications: PropTypes.arrayOf(PropTypes.object),
      resourceTypes: PropTypes.arrayOf(PropTypes.object),
      dateRangeStart: PropTypes.string,
      dateRangeEnd: PropTypes.string,
    }).isRequired,
    sort: PropTypes.shape({
      criteria: PropTypes.string,
      pageNumber: PropTypes.number,
      pageSize: PropTypes.number,
    }),
    loading: PropTypes.bool,
    results: PropTypes.arrayOf(PropTypes.object),
    totalCount: PropTypes.number,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = ({ calqSearch }) => ({
  calqSearch,
});

Results.displayName = 'Results';

export default connect(mapStateToProps)(Results);
